import { CommonRequest, CommonResponse, Method, ServiceName } from 'models/common/RestApi';
import { callApi } from 'utils/ApiUtil';
import {
  SetupSchedule,
  SetupScheduleCondition,
  SetupTargetStepScheduleRequest,
} from 'models/setup/SetupSchedule';
import { Employee } from '../../models/admin/Employee';
import { ApproveRequestPageType } from 'pages/approves/ApproveRequestDetailCase';

/**
 * 프로젝트 셋업 관리 > 프로젝트 일정 조회
 * @param condition
 */
export const findSchedule = async (condition?: SetupScheduleCondition) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/schedule`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as SetupSchedule[];
};

/**
 * 프로젝트 셋업 관리 > 일정 현황 조회
 * @param elmPrjId
 * @param excpId
 */
export const findScheduleStatus = async (elmPrjId: string, excpId?: string, tgtEqpId?: string) => {
  const condition = {
    elmPrjId: elmPrjId,
    excpId: excpId || '',
    tgtEqpId: tgtEqpId || '',
  };
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/schedule/status`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as any[];
};

/**
 * 프로젝트 셋업 관리 > 일정 현황 상세 조회
 * @param elmPrjId
 * @param spTpCd
 * @param excpId
 * @param wkId
 * @param tgtEqpId
 */
export const findScheduleStatusDetail = async (
  elmPrjId: string,
  spTpCd?: string,
  excpId?: string,
  wkId?: string,
  tgtEqpId?: string
) => {
  const condition = {
    elmPrjId: elmPrjId,
    excpId: excpId || '',
    spTpCd: spTpCd || '',
    wkId: wkId || '',
    tgtEqpId: tgtEqpId || '',
  };
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/schedule/status-detail`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : undefined) as any;
};

/**
 * 프로젝트 협력사 정보 조회
 * @param elmPrjId
 * @param excpId
 */
export const findExcpByElmPrjId = async (elmPrjId: string, excpId?: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/${elmPrjId}/excp`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ elmPrjId: elmPrjId, excpId: excpId || '' }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as SetupSchedule[];
};

/**
 * 프로젝트 결재자 정보 목록 조히
 * @param elmPrjId
 */
export const findRequestApproved = async (elmPrjId: string) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/${elmPrjId}/request-approved`,
    serviceName: ServiceName.ELM_BE,
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as Employee[];
};

/**
 * 셋업일정 결재 요청 가능 상태 확인
 * @param aprReqTpCd  (=pageId)
 * @param elmPrjId
 * @param excpId
 * @param aprReqId
 * @param requestList   일정변경요청인 경우 변경요청 대상 설비 목록
 */
export const checkAvailableRequest = async (
  aprReqTpCd: string | ApproveRequestPageType,
  elmPrjId: string,
  excpId: string,
  aprReqId?: string,
  requestList?: any[]
) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/${elmPrjId}/check-request`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: {
      aprReqTpCd: aprReqTpCd,
      elmPrjId: elmPrjId,
      excpId: excpId,
      aprReqId: aprReqId || '',
      requestList: requestList,
    },
  };

  const response: CommonResponse<SetupSchedule> = await callApi(request);
  return response;
};

/**
 * 셋업일정 결재 요청 가능 설비 확인
 * @param elmPrjId
 * @param excpId
 * @param tgtEqpIds 요청대상 설비ID 목록
 * @param aprReqId  현재 결재문서 ID (추가하는 경우 현재결재ID에 포함된 설비는 제외)
 */
export const checkAvailableRequestEquipment = async (
  elmPrjId: string,
  excpId: string,
  tgtEqpIds: string[],
  aprReqId?: string
) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/${elmPrjId}/check-request-equipment`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: {
      elmPrjId: elmPrjId,
      excpId: excpId,
      tgtEqpIds: tgtEqpIds,
      aprReqId: aprReqId || '',
    },
  };

  const response: CommonResponse<SetupSchedule[]> = await callApi(request);
  return response;
};

/**
 * 셋업일정 요청 목록 조회
 *
 * @param aprReqId
 * @param elmPrjId
 * @param excpId
 * @param spTpCd
 * @param requestList   일정변경요청인 경우 변경요청 대상 설비 목록 (임시저장 전 상태 사용)
 */
export const findScheduleRequest = async (
  aprReqId: string,
  elmPrjId: string,
  excpId: string,
  spTpCd?: string,
  requestList?: any[]
) => {
  const condition = {
    aprReqId: aprReqId,
    elmPrjId: elmPrjId,
    excpId: excpId,
    spTpCd: spTpCd || '',
    requestList: requestList,
  };
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/schedule/request`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: condition,
  };

  const response: CommonResponse = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : undefined) as any;
};

/**
 * 실적 불러오기 (삭제 후 재등록)
 * @param elmPrjId
 * @param excpId
 * @param wkId
 * @param crtnDt
 */
export const reloadScheduleResult = async (elmPrjId, excpId, wkId, crtnDt) => {
  const condition = {
    elmPrjId: elmPrjId,
    excpId: excpId,
    wkId: wkId,
    crtnDt: crtnDt,
  };
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/schedule/result`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: condition,
  };

  const response: CommonResponse = await callApi(request);
  return response;
};

/**
 * 셋업일정 실적 저장 (작업일보 실적 저장)
 */
export const saveScheduleResult = async (elmPrjId, excpId, wkId, requestList) => {
  const condition = {
    elmPrjId: elmPrjId,
    excpId: excpId,
    wkId: wkId,
    requestList: requestList,
  };
  const request: CommonRequest = {
    method: Method.PUT,
    url: `/v1/setup/schedule/result`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: condition,
  };

  const response: CommonResponse = await callApi(request);
  return response;
};

/**
 * 셋업실적완료요청 목록 조회
 * @param elmPrjId
 * @param excpId
 */
export const findSetupResultRequest = async (elmPrjId?: string, excpId?: string) => {
  const condition = {
    elmPrjId: elmPrjId || '',
    excpId: excpId || '',
  };
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/result/request`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse<SetupTargetStepScheduleRequest[]> = await callApi(request);
  return (
    response.successOrNot === 'Y' ? response.data : undefined
  ) as SetupTargetStepScheduleRequest[];
};

/**
 * 셋업실적완료요청 이력 목록 조회
 * @param elmPrjId
 * @param excpId
 */
export const findSetupResultRequestHistory = async (elmPrjId: string, excpId?: string) => {
  const condition = {
    elmPrjId: elmPrjId,
    excpId: excpId || '',
  };
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/result/request-history`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...condition }),
  };

  const response: CommonResponse<SetupTargetStepScheduleRequest[]> = await callApi(request);
  return (
    response.successOrNot === 'Y' ? response.data : undefined
  ) as SetupTargetStepScheduleRequest[];
};

/**
 * 셋업실적완료 요청서 생성
 *
 * @param elmPrjId
 * @param excpId
 */
export const createRequestSetupResultRequest = async (elmPrjId: string, excpId: string) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/result/create-request`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: {
      elmPrjId: elmPrjId,
      excpId: excpId,
    },
  };

  const response: CommonResponse = await callApi(request);
  return response;
};

/**
 * 셋업실적완료 요청 처리
 * @param items
 */
export const requestSetupResultRequest = async (items: SetupTargetStepScheduleRequest[]) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/result/request`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: items,
  };

  const response: CommonResponse<SetupTargetStepScheduleRequest[]> = await callApi(request);
  return response;
};

/**
 * 셋업실적완료 재요청 처리
 * @param item
 */
export const reRequestSetupResultRequest = async (item: SetupTargetStepScheduleRequest) => {
  const request: CommonRequest = {
    method: Method.POST,
    url: `/v1/setup/result/re-request`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: item,
  };

  const response: CommonResponse<SetupTargetStepScheduleRequest> = await callApi(request);
  return response;
};

/**
 * 셋업실적완료 요청 승인 처리
 * @param items
 */
export const approveSetupResultRequest = async (items: SetupTargetStepScheduleRequest[]) => {
  const request: CommonRequest = {
    method: Method.PUT,
    url: `/v1/setup/result/request`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: items,
  };

  const response: CommonResponse<SetupTargetStepScheduleRequest[]> = await callApi(request);
  return response;
};

/**
 * 셋업실적완료 요청 반려 처리
 * @param items
 */
export const rejectSetupResultRequest = async (items: SetupTargetStepScheduleRequest[]) => {
  const request: CommonRequest = {
    method: Method.PATCH,
    url: `/v1/setup/result/request`,
    serviceName: ServiceName.ELM_BE,
    bodyParams: items,
  };

  const response: CommonResponse<SetupTargetStepScheduleRequest[]> = await callApi(request);
  return response;
};

/**
 * 프로젝트 셋업 관리 > 일정 지연 현황 조회
 * @param params
 */
export const findScheduleDelayStatus = async (params: any) => {
  const request: CommonRequest = {
    method: Method.GET,
    url: `/v1/setup/schedule/delay-status`,
    serviceName: ServiceName.ELM_BE,
    queryParams: new URLSearchParams({ ...(params || {}) }),
  };

  const response: CommonResponse<[]> = await callApi(request);
  return (response.successOrNot === 'Y' ? response.data : []) as any[];
};
